import React, {
  Suspense,
  Fragment,
  lazy
} from 'react';
import {
  Switch,
  Redirect,
  Route
} from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import DocsLayout from 'src/layouts/DocsLayout';
import MainLayout from 'src/layouts/MainLayout';
import HomeView from 'src/views/home/HomeView';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes
                    ? renderRoutes(route.routes)
                    : <Component {...props} />}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/account',
        component: lazy(() => import('src/views/account/AccountView'))
      },
      {
        exact: true,
        path: '/app/calendar',
        component: lazy(() => import('src/views/calendar/CalendarView'))
      },
      {
        exact: true,
        path: [
          '/app/chat/new',
          '/app/chat/:threadKey'
        ],
        component: lazy(() => import('src/views/chat/ChatView'))
      },
      {
        exact: true,
        path: '/app/chat',
        component: () => <Redirect to="/app/chat/new" />
      },
      {
        exact: true,
        path: '/app/extra/charts/apex',
        component: lazy(() => import('src/views/extra/charts/ApexChartsView'))
      },
      {
        exact: true,
        path: '/app/extra/forms/formik',
        component: lazy(() => import('src/views/extra/forms/FormikView'))
      },
      {
        exact: true,
        path: '/app/extra/forms/redux',
        component: lazy(() => import('src/views/extra/forms/ReduxFormView'))
      },
      {
        exact: true,
        path: '/app/extra/editors/draft-js',
        component: lazy(() => import('src/views/extra/editors/DraftEditorView'))
      },
      {
        exact: true,
        path: '/app/extra/editors/quill',
        component: lazy(() => import('src/views/extra/editors/QuillEditorView'))
      },
      {
        exact: true,
        path: '/app/kanban',
        component: lazy(() => import('src/views/kanban/KanbanView'))
      },
      {
        exact: true,
        path: [
          '/app/mail/label/:customLabel/:mailId?',
          '/app/mail/:systemLabel/:mailId?'
        ],
        component: lazy(() => import('src/views/mail/MailView'))
      },
      {
        exact: true,
        path: '/app/mail',
        component: () => <Redirect to="/app/mail/all" />
      },
      {
        exact: true,
        path: '/app/management/branches',
        component: lazy(() => import('src/views/branch/BranchListView'))
      },
      {
        exact: true,
        path: '/app/management/branches/new',
        component: lazy(() => import('src/views/branch/BranchNewView'))
      },
      {
        exact: true,
        path: '/app/management/branches/:branchId/edit',
        component: lazy(() => import('src/views/branch/BranchEditView'))
      },
      {
        exact: true,
        path: '/app/management/customers',
        component: lazy(() => import('src/views/customer/CustomerListView'))
      },
      {
        exact: true,
        path: '/app/management/customers/new',
        component: lazy(() => import('src/views/customer/CustomerNewView'))
      },
      {
        exact: true,
        path: '/app/management/customers/:customerId',
        component: lazy(() => import('src/views/customer/CustomerDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/customers/:customerId/edit',
        component: lazy(() => import('src/views/customer/CustomerEditView'))
      },
      {
        exact: true,
        path: '/app/management/invoices',
        component: lazy(() => import('src/views/invoice/InvoiceListView'))
      },
      {
        exact: true,
        path: '/app/management/invoices/:invoiceId',
        component: lazy(() => import('src/views/invoice/InvoiceDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/cashup-reports',
        component: lazy(() => import('src/views/cashupReport/CashupReportListView'))
      },
      {
        exact: true,
        path: '/app/management/cashup-reports/:floatId',
        component: lazy(() => import('src/views/cashupReport/CashupReportDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/purchaseNote',
        component: lazy(() => import('src/views/purchaseNote/PurchaseNoteListView'))
      },
      {
        exact: true,
        path: '/app/management/purchaseNote/:purchaseId',
        component: lazy(() => import('src/views/purchaseNote/PurchaseNoteDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/orders',
        component: lazy(() => import('src/views/order/OrderListView'))
      },
      {
        exact: true,
        path: '/app/management/orders/:orderId',
        component: lazy(() => import('src/views/order/OrderDetailsView'))
      },
      {
        exact: true,
        path: '/app/pos/allexpenses/',
        component: lazy(() => import('src/views/pos/PosExpensesListAllView'))
      },
      {
        exact: true,
        path: '/app/pos/allloans/',
        component: lazy(() => import('src/views/pos/POSLoan/PosLoanListAllView'))
      },
      {
        exact: true,
        path: '/app/pos/expense/:expenseId',
        component: lazy(() => import('src/views/pos/PosExpenseEditView'))
      },
      {
        exact: true,
        path: '/app/pos/allsales/',
        component: lazy(() => import('src/views/pos/PosSaleListAllView'))
      },
      {
        exact: true,
        path: '/app/pos/sales/',
        component: lazy(() => import('src/views/pos/PosSaleListOnlyTillView'))
      },
      {
        exact: true,
        path: '/app/pos/sales/new',
        component: lazy(() => import('src/views/pos/PosSaleView'))
      },
      {
        exact: true,
        path: '/app/pos/sales/:saleOrderId/edit',
        component: lazy(() => import('src/views/pos/PosSaleView'))
      },
      {
        exact: true,
        path: '/app/pos/purchases/',
        component: lazy(() => import('src/views/pos/PosPurchaseListOnlyTillView'))
      },
      {
        exact: true,
        path: '/app/pos/allpurchases/',
        component: lazy(() => import('src/views/pos/PosPurchaseListAllView'))
      },
      {
        exact: true,
        path: '/app/pos/purchases/new',
        component: lazy(() => import('src/views/pos/PosPurchaseView'))
      },
      {
        exact: true,
        path: '/app/pos/purchases/:purchaseOrderId/edit',
        component: lazy(() => import('src/views/pos/PosPurchaseView'))
      },
      {
        exact: true,
        path: '/app/pos/weighbridge/new',
        component: lazy(() => import('src/views/pos/PosWeighbridgeNoteView'))
      },
      {
        exact: true,
        path: '/app/pos/weighbridge/:wnId/edit',
        component: lazy(() => import('src/views/pos/PosWeighbridgeNoteView'))
      },
      {
        exact: true,
        path: '/app/pos/weighbridge/',
        component: lazy(() => import('src/views/pos/PosWeighbridgeListOnlyTillView'))
      },
      {
        exact: true,
        path: '/app/pos/allweighbridge/',
        component: lazy(() => import('src/views/pos/PosWeighbridgeListAllView'))
      },
      {
        exact: true,
        path: '/app/management/weighbridge/:wnId',
        component: lazy(() => import('src/views/weighbridgeNote/WeighbridgeNoteDetailsView'))
      },
      {
        exact: true,
        path: '/app/pos/float/list',
        component: lazy(() => import('src/views/pos/PosFloatListView'))
      },
      {
        exact: true,
        path: '/app/pos/float/new',
        component: lazy(() => import('src/views/pos/PosFloatView'))
      },
      {
        exact: true,
        path: '/app/pos/float/expense',
        component: lazy(() => import('src/views/pos/PosExpenseView'))
      },
      {
        exact: true,
        path: '/app/pos/float/cashup',
        component: lazy(() => import('src/views/pos/PosCashupView'))
      },
      {
        exact: true,
        path: '/app/pos/float/continue',
        component: lazy(() => import('src/views/pos/PosFloatContinueView'))
      },
      {
        exact: true,
        path: '/app/pos/float/received',
        component: lazy(() => import('src/views/pos/PosFloatReceivedView'))
      },
      {
        exact: true,
        path: '/app/pos/float/issued',
        component: lazy(() => import('src/views/pos/PosFloatIssuedView'))
      },
      {
        exact: true,
        path: '/app/pos/cashregister/new',
        component: lazy(() => import('src/views/pos/PosCashRegisterView'))
      },
      {
        exact: true,
        path: '/app/pos/loan/new',
        component: lazy(() => import('src/views/pos/PosLoanIssueView'))
      },
      {
        exact: true,
        path: '/app/pos/loan/settleloan',
        component: lazy(() => import('src/views/pos/PosLoanSettleView'))
      },
      {
        exact: true,
        path: '/app/management/products',
        component: lazy(() => import('src/views/product/ProductListView'))
      },
      {
        exact: true,
        path: '/app/management/products/create',
        component: lazy(() => import('src/views/product/ProductCreateView'))
      },
      {
        exact: true,
        path: '/app/management/products/:productId/edit',
        component: lazy(() => import('src/views/product/ProductEditView'))
      },
      {
        exact: true,
        path: '/app/management/categories/create',
        component: lazy(() => import('src/views/product/CategoryCreateView'))
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/customers" />
      },
      {
        exact: true,
        path: '/app/management/users',
        component: lazy(() => import('src/views/user/UserListView'))
      },
      {
        exact: true,
        path: '/app/management/users/new',
        component: lazy(() => import('src/views/user/UserCreateView'))
      },
      {
        exact: true,
        path: '/app/management/users/:userId',
        component: lazy(() => import('src/views/user/UserDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/users/:userId/edit',
        component: lazy(() => import('src/views/user/UserEditView'))
      },
      {
        exact: true,
        path: '/app/projects/overview',
        component: lazy(() => import('src/views/project/OverviewView'))
      },
      {
        exact: true,
        path: '/app/projects/browse',
        component: lazy(() => import('src/views/project/ProjectBrowseView'))
      },
      {
        exact: true,
        path: '/app/projects/create',
        component: lazy(() => import('src/views/project/ProjectCreateView'))
      },
      {
        exact: true,
        path: '/app/projects/:id',
        component: lazy(() => import('src/views/project/ProjectDetailsView'))
      },
      {
        exact: true,
        path: '/app/projects',
        component: () => <Redirect to="/app/projects/browse" />
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() => import('src/views/reports/DashboardView'))
      },
      {
        exact: true,
        path: '/app/reports/purchase-per-transaction',
        component: lazy(() => import('src/views/reports/PurchasePerTransactionView'))
      },
      {
        exact: true,
        path: '/app/reports/sale-per-transaction',
        component: lazy(() => import('src/views/reports/SalesPerTransactionView'))
      },
      {
        exact: true,
        path: '/app/reports/stock-per-transaction',
        component: lazy(() => import('src/views/reports/StockPerTransactionView'))
      },
      {
        exact: true,
        path: '/app/reports/stock-per-transaction/pdf',
        component: lazy(() => import('src/views/reports/StockPerTransactionPaperView'))
      },
      {
        exact: true,
        path: '/app/reports/product-summary',
        component: lazy(() => import('src/views/reports/ProductSummaryView'))
      },
      {
        exact: true,
        path: '/app/reports/float-per-transaction',
        component: lazy(() => import('src/views/reports/FloatPerTransactionView'))
      },
      {
        exact: true,
        path: '/app/reports/cashup-reports',
        component: lazy(() => import('src/views/reports/CashupReportsView'))
      },
      {
        exact: true,
        path: '/app/reports/weighbridge-per-transaction',
        component: lazy(() => import('src/views/reports/WeighbridgePerTransactionView'))
      },
      {
        exact: true,
        path: '/app/reports/customer-report',
        component: lazy(() => import('src/views/reports/CustomerReportView'))
      },
      {
        exact: true,
        path: '/app/reports/dashboard-alternative',
        component: lazy(() => import('src/views/reports/DashboardAlternativeView'))
      },
      {
        exact: true,
        path: '/app/reports',
        component: () => <Redirect to="/app/reports/dashboard" />
      },
      {
        exact: true,
        path: '/app/social/feed',
        component: lazy(() => import('src/views/social/FeedView'))
      },
      {
        exact: true,
        path: '/app/social/profile',
        component: lazy(() => import('src/views/social/ProfileView'))
      },
      {
        exact: true,
        path: '/app/social',
        component: () => <Redirect to="/app/social/profile" />
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/reports/dashboard" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/docs',
    layout: DocsLayout,
    routes: [
      {
        exact: true,
        path: '/docs',
        component: () => <Redirect to="/docs/welcome" />
      },
      {
        exact: true,
        path: '/docs/welcome',
        component: lazy(() => import('src/views/docs/WelcomeView'))
      },
      {
        exact: true,
        path: '/docs/getting-started',
        component: lazy(() => import('src/views/docs/GettingStartedView'))
      },
      {
        exact: true,
        path: '/docs/environment-variables',
        component: lazy(() => import('src/views/docs/EnvironmentVariablesView'))
      },
      {
        exact: true,
        path: '/docs/deployment',
        component: lazy(() => import('src/views/docs/DeploymentView'))
      },
      {
        exact: true,
        path: '/docs/api-calls',
        component: lazy(() => import('src/views/docs/APICallsView'))
      },
      {
        exact: true,
        path: '/docs/analytics',
        component: lazy(() => import('src/views/docs/AnalyticsView'))
      },
      {
        exact: true,
        path: '/docs/authentication',
        component: lazy(() => import('src/views/docs/AuthenticationView'))
      },
      {
        exact: true,
        path: '/docs/routing',
        component: lazy(() => import('src/views/docs/RoutingView'))
      },
      {
        exact: true,
        path: '/docs/settings',
        component: lazy(() => import('src/views/docs/SettingsView'))
      },
      {
        exact: true,
        path: '/docs/state-management',
        component: lazy(() => import('src/views/docs/StateManagementView'))
      },
      {
        exact: true,
        path: '/docs/theming',
        component: lazy(() => import('src/views/docs/ThemingView'))
      },
      {
        exact: true,
        path: '/docs/support',
        component: lazy(() => import('src/views/docs/SupportView'))
      },
      {
        exact: true,
        path: '/docs/changelog',
        component: lazy(() => import('src/views/docs/ChangelogView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: '/',
        component: HomeView
      },
      // {
      //   exact: true,
      //   path: '/pricing',
      //   component: lazy(() => import('src/views/pricing/PricingView'))
      // },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
